<template>
  <nav
    class="ls-index"
    aria-label="Sections"
  >
    <ul class="ls-index__list">
      <li
        v-for="section in sections"
        :key="section.key"
      >
        <a
          class="ls-index__link"
          :href="`#ls-sec-${section.key}`"
          :aria-current="section.key === activeKey ? 'true' : null"
          @click="$emit('jump', section.key)"
        >
          <span class="ls-index__title">{{ section.title }}</span>
          <span class="ls-index__count">{{ section.total }}</span>
        </a>
      </li>
    </ul>
    <button
      type="button"
      class="ls-index__browse"
      data-test="ls-browse-all"
      @click="$emit('browse-all')"
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
      ><path
        d="M4 6h16M7 12h10M10 18h4"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
      /></svg>
      Search and filter all styles
    </button>
  </nav>
</template>

<script>
export default {
  name: 'LsSectionIndex',
  props: {
    sections: { type: Array, default: () => [] },
    activeKey: { type: String, default: '' },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

// Mobile and tablet: a sticky chip rail under the masthead. Desktop: a sticky
// index in the left column (BrandedLineSheet places it).
.ls-index {
  position: sticky;
  top: 56px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px $ls-gutter;
  background: var(--ls-ground);
  border-bottom: 1px solid var(--ls-line);
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &__list {
    display: flex;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      scroll-snap-align: start;
      flex: none;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 14px;
    border: 1px solid var(--ls-line);
    border-radius: 999px;
    color: var(--ls-ink);
    font: 500 0.875rem/1 var(--ls-body-font);
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.2s $ls-ease, border-color 0.2s $ls-ease;
    @include ls-focus;

    &:hover {
      border-color: var(--ls-ink);
    }

    &[aria-current='true'] {
      background: var(--ls-ink);
      border-color: var(--ls-ink);
      color: var(--ls-ground);

      .ls-index__count {
        color: inherit;
      }
    }
  }

  &__count {
    color: var(--ls-muted);
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
  }

  &__browse {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 14px;
    border: 0;
    background: transparent;
    color: var(--ls-ink);
    font: 500 0.875rem/1 var(--ls-body-font);
    text-decoration: underline;
    text-underline-offset: 3px;
    white-space: nowrap;
    cursor: pointer;
    @include ls-focus;
  }

  @include ls-up($ls-xl) {
    top: 88px;
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    padding: 0;
    border-bottom: 0;
    background: none;
    overflow: visible;

    &__list {
      flex-direction: column;
      gap: 2px;
    }

    &__link {
      justify-content: space-between;
      width: 100%;
      min-height: 40px;
      padding: 0 12px;
      border-color: transparent;
      border-radius: var(--ls-radius);
    }

    &__browse {
      justify-content: flex-start;
      padding: 0 12px;
      white-space: normal;
      text-align: left;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-index__link {
    transition: none;
  }
}
</style>
